import {
    FormControl,
    FormHelperText,
    FormLabel,
    Select,
    VStack,
} from '@chakra-ui/react';
import type { InvoiceType } from '../../types/invoice';
import { useGetInvoiceCcVendors } from '../../hooks/invoice/useGetInvoiceCcVendors';

export interface InvoiceUploadFormProps {
    invoiceType: InvoiceType;
    onInvoiceTypeChange: (value: InvoiceType) => void;
    /** When true, show optional credit-card vendor picker (single received/sublet uploads only). */
    showCcVendorSelect?: boolean;
    ccVendorId?: number | null;
    onCcVendorChange?: (value: { ccVendorId: number | null; ccVendorName: string | null }) => void;
}

const InvoiceUploadForm = ({
    invoiceType,
    onInvoiceTypeChange,
    showCcVendorSelect = false,
    ccVendorId = null,
    onCcVendorChange,
}: InvoiceUploadFormProps) => {
    const showCc =
        showCcVendorSelect &&
        (invoiceType === 'received' || invoiceType === 'sublet');
    const {
        data: ccVendors = [],
        isLoading: isLoadingCcVendors,
        isError: isCcVendorsError,
        error: ccVendorsError,
        refetch: refetchCcVendors,
        isFetching: isFetchingCcVendors,
    } = useGetInvoiceCcVendors(showCc);

    return (
        <VStack spacing={4} align="stretch">
            <FormControl>
                <FormLabel>Invoice Type</FormLabel>
                <Select
                    value={invoiceType}
                    onChange={(e) => onInvoiceTypeChange(e.target.value as InvoiceType)}
                >
                    <option value="received">Received</option>
                    <option value="returned">Credited</option>
                    <option value="return_slip">Return Slip</option>
                    <option value="sublet">Sublet</option>
                </Select>
            </FormControl>

            {showCc && (
                <FormControl isInvalid={isCcVendorsError}>
                    <FormLabel>Credit Card</FormLabel>
                    <Select
                        value={ccVendorId != null ? String(ccVendorId) : ''}
                        onChange={(e) => {
                            const raw = e.target.value;
                            if (!raw) {
                                onCcVendorChange?.({ ccVendorId: null, ccVendorName: null });
                                return;
                            }
                            const id = Number(raw);
                            const match = ccVendors.find((v) => v.vendorID === id);
                            onCcVendorChange?.({
                                ccVendorId: id,
                                ccVendorName: match?.name ?? match?.searchName ?? null,
                            });
                        }}
                        isDisabled={isLoadingCcVendors || isFetchingCcVendors}
                    >
                        <option value="">
                            {isLoadingCcVendors || isFetchingCcVendors
                                ? 'Loading credit cards…'
                                : 'No credit card'}
                        </option>
                        {ccVendors.map((v) => (
                            <option key={v.vendorID} value={String(v.vendorID)}>
                                {v.name || v.searchName}
                            </option>
                        ))}
                    </Select>
                    {isCcVendorsError && (
                        <FormHelperText color="red.500">
                            Couldn’t load credit cards
                            {ccVendorsError instanceof Error && ccVendorsError.message
                                ? `: ${ccVendorsError.message}`
                                : ''}
                            .{' '}
                            <button
                                type="button"
                                onClick={() => {
                                    void refetchCcVendors();
                                }}
                                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                            >
                                Retry
                            </button>
                        </FormHelperText>
                    )}
                    {!isCcVendorsError &&
                        !isLoadingCcVendors &&
                        !isFetchingCcVendors &&
                        ccVendors.length === 0 && (
                            <FormHelperText>
                                No credit cards were returned for this organization.
                            </FormHelperText>
                        )}
                </FormControl>
            )}
        </VStack>
    );
};

export default InvoiceUploadForm;
